import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Users,
  Info,
} from "lucide-react";

const MeetingRoomsVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Possible",
      meetings: [
        [1, 4],
        [7, 10],
        [10, 15],
      ],
      expected: true,
    },
    {
      id: 2,
      name: "Impossible",
      meetings: [
        [2, 4],
        [9, 12],
        [6, 10],
      ],
      expected: false,
    },
    {
      id: 3,
      name: "Touching meetings",
      meetings: [
        [1, 5],
        [5, 10],
        [10, 15],
      ],
      expected: true,
    },
    {
      id: 4,
      name: "Overlap at start",
      meetings: [
        [1, 10],
        [2, 5],
      ],
      expected: false,
    },
    {
      id: 5,
      name: "Complex possible case",
      meetings: [
        [13, 15],
        [1, 4],
        [8, 12],
        [5, 7],
      ],
      expected: true,
    },
    {
      id: 6,
      name: "Single meeting",
      meetings: [[5, 10]],
      expected: true,
    },
    {
      id: 7,
      name: "Empty",
      meetings: [],
      expected: true,
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [meetings, setMeetings] = useState(testCases[0].meetings);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);

  // Pseudo code
  const pseudoCode = [
    { line: "function canAttendMeetings(meetings):", indent: 0 },
    { line: "// Sort by start time", indent: 1 },
    { line: "meetings.sort((a, b) => a[0] - b[0])", indent: 1 },
    { line: "", indent: 0 },
    { line: "for i = 1 to meetings.length - 1:", indent: 1 },
    { line: "// Check for overlap with previous meeting", indent: 2 },
    { line: "if meetings[i][0] < meetings[i-1][1]:", indent: 2 },
    { line: "// Overlap found!", indent: 3 },
    { line: "return false", indent: 3 },
    { line: "", indent: 0 },
    { line: "// No overlaps found after checking all", indent: 1 },
    { line: "return true", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (inputMeetings) => {
    if (inputMeetings.length <= 1) {
      return [
        {
          step: 0,
          meetings: inputMeetings,
          description: "Not enough meetings to have an overlap.",
          highlightLine: 11,
          result: true,
          isFinal: true,
        },
      ];
    }

    const allSteps = [];
    let stepCount = 0;

    allSteps.push({
      step: stepCount++,
      meetings: [...inputMeetings],
      pointers: {},
      description: "Original unsorted meetings.",
      highlightLine: 0,
    });

    const sortedMeetings = [...inputMeetings].sort((a, b) => a[0] - b[0]);
    allSteps.push({
      step: stepCount++,
      meetings: [...sortedMeetings],
      pointers: {},
      description: "Sort all meetings by their start time.",
      highlightLine: 2,
    });

    for (let i = 1; i < sortedMeetings.length; i++) {
      const currentMeeting = sortedMeetings[i];
      const prevMeeting = sortedMeetings[i - 1];

      allSteps.push({
        step: stepCount++,
        meetings: [...sortedMeetings],
        pointers: { current: i, prev: i - 1 },
        description: `Compare current meeting [${currentMeeting.join(
          ","
        )}] with previous [${prevMeeting.join(",")}]`,
        highlightLine: 5,
        comparison: {
          condition: `${currentMeeting[0]} < ${prevMeeting[1]}`,
          result: currentMeeting[0] < prevMeeting[1],
        },
      });

      if (currentMeeting[0] < prevMeeting[1]) {
        allSteps.push({
          step: stepCount++,
          meetings: [...sortedMeetings],
          pointers: { current: i, prev: i - 1, overlap: true },
          description: `Overlap found! Start time ${currentMeeting[0]} is before previous end time ${prevMeeting[1]}.`,
          highlightLine: 7,
          result: false,
          isFinal: true,
        });
        return allSteps;
      }
    }

    allSteps.push({
      step: stepCount++,
      meetings: [...sortedMeetings],
      pointers: {},
      description:
        "All meetings checked, no overlaps found. It's possible to attend all.",
      highlightLine: 11,
      result: true,
      isFinal: true,
    });

    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(meetings);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setResult(null);
  }, [meetings]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      const step = steps[nextStep];
      setCurrentStep(nextStep);
      setHighlightedLine(step.highlightLine);
      if (step.result !== undefined) setResult(step.result);
      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const step = steps[prevStep];
      setCurrentStep(prevStep);
      setHighlightedLine(step.highlightLine);
      setFinished(false);
      setResult(null);
      if (step.result !== undefined) setResult(step.result);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
    setResult(null);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setMeetings(testCases[index].meetings);
  };

  const addCustomTestCase = () => {
    try {
      const parsedMeetings = JSON.parse(customInput);
      if (
        !Array.isArray(parsedMeetings) ||
        !parsedMeetings.every(
          (m) =>
            Array.isArray(m) &&
            m.length === 2 &&
            typeof m[0] === "number" &&
            typeof m[1] === "number"
        )
      ) {
        throw new Error("Invalid format.");
      }

      const sorted = [...parsedMeetings].sort((a, b) => a[0] - b[0]);
      let expected = true;
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i][0] < sorted[i - 1][1]) {
          expected = false;
          break;
        }
      }

      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 6}`,
        meetings: parsedMeetings,
        expected,
      };

      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert("Invalid format. Use a valid JSON array like [[1, 4], [7, 10]]");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  const generateTimeline = () => {
    if (!currentStepData?.meetings || currentStepData.meetings.length === 0)
      return null;
    const allValues = currentStepData.meetings.flat();
    const minVal = Math.min(0, ...allValues);
    const maxVal = Math.max(10, ...allValues);
    const range = maxVal - minVal;
    if (range === 0) return { minVal, maxVal, scale: 10 };
    const scale = 100 / (range + 1);
    return { minVal, maxVal, scale };
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Users className="text-blue-500" size={24} />
        Meeting Rooms
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an array of meeting time intervals, determine if a person
                could attend all meetings. A person can attend only one meeting
                at a time.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75 truncate">
                      Meetings: [
                      {tc.meetings.map((m) => `[${m.join(",")}]`).join(", ")}]
                    </div>
                    <div className="text-xs opacity-75 truncate">
                      Expected: {tc.expected ? "Possible" : "Impossible"}
                    </div>
                  </button>
                  {tc.id > 7 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom meetings: [[1, 4], [5, 6]]"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>
            {currentStepData?.comparison && (
              <div className="mb-3 p-2 bg-yellow-50 rounded">
                <p className="text-xs text-yellow-800 font-medium">
                  Checking: {currentStepData.comparison.condition} is{" "}
                  {currentStepData.comparison.result.toString()}
                </p>
              </div>
            )}

            <div className="relative h-64 bg-gray-50 rounded p-2 overflow-hidden">
              {generateTimeline() &&
                currentStepData.meetings.map((meeting, idx) => {
                  const { minVal, scale } = generateTimeline();
                  const isCurrent = currentStepData.pointers?.current === idx;
                  const isPrev = currentStepData.pointers?.prev === idx;
                  const isOverlap =
                    currentStepData.pointers?.overlap && (isCurrent || isPrev);

                  let colorClasses = "bg-gray-300 border-gray-400";
                  if (highlightedLine >= 5) {
                    colorClasses = "bg-green-200 border-green-400";
                  }
                  if (isCurrent)
                    colorClasses = "bg-yellow-300 border-yellow-500";
                  if (isPrev) colorClasses = "bg-blue-300 border-blue-500";
                  if (isOverlap)
                    colorClasses = "bg-red-300 border-red-500 animate-pulse";

                  return (
                    <div
                      key={idx}
                      className={`absolute h-8 rounded transition-all duration-300 flex items-center justify-center text-xs font-mono border ${colorClasses}`}
                      style={{
                        left: `${(meeting[0] - minVal) * scale}%`,
                        width: `${
                          Math.max(0.5, meeting[1] - meeting[0]) * scale
                        }%`,
                        top: `${20 + idx * 15}%`,
                      }}
                    >
                      [{meeting[0]},{meeting[1]}]
                    </div>
                  );
                })}
            </div>

            {finished && result !== null && (
              <div
                className={`mt-3 p-3 rounded ${
                  result ? "bg-green-100" : "bg-red-100"
                }`}
              >
                <h4
                  className={`text-sm font-bold ${
                    result ? "text-green-900" : "text-red-900"
                  }`}
                >
                  Result:{" "}
                  {result
                    ? "Possible to attend all meetings"
                    : "Impossible to attend all meetings"}
                </h4>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The core idea is to sort the meetings by their start times. Then,
              iterate through the sorted meetings and check if any meeting's
              start time is before the previous meeting's end time. If so, an
              overlap exists.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time Complexity:</span> O(N log N)
                - due to sorting.
              </div>
              <div>
                <span className="font-medium">Space Complexity:</span> O(1) or
                O(N) - depending on sort implementation.
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Sorting by start time is the essential first step.</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Once sorted, you only need to check adjacent meetings for
                  overlaps.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  A meeting starting exactly when another ends (e.g., [1,5] and
                  [5,10]) is not an overlap.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-300 border border-blue-500 rounded"></div>
                <span>Previous meeting being checked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-yellow-300 border border-yellow-500 rounded"></div>
                <span>Current meeting being checked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-200 border border-green-400 rounded"></div>
                <span>Checked, no overlap</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-red-300 border border-red-500 rounded"></div>
                <span>Overlap Found!</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingRoomsVisualizer;
